import React, { useState } from 'react';
import { FileText, Eye, CheckCircle, CreditCard, Info, X } from 'lucide-react';

const steps = [
  {
    icon: FileText,
    title: 'Submit Your Request',
    description: 'Fill out our online quote form with your property details and service requirements.',
    hasTooltip: false,
  },
  {
    icon: Eye,
    title: 'Property Assessment',
    description: 'We arrange a time to view your property and finalise the quote, taking all aspects into account.',
    hasTooltip: true,
    tooltipText: 'We take into consideration access, elevation of the property, rock garden beds and walkways (if loose we will discuss the potential of broken windows) and any other obstacles.',
  },
  {
    icon: CheckCircle,
    title: 'Quote Confirmation',
    description: 'Receive your detailed quote. All quotes are subject to review to ensure accuracy.',
    hasTooltip: false,
  },
  {
    icon: CreditCard,
    title: 'Service & Payment',
    description: 'We complete the work to the highest standard. All invoices have a 7-day payment term.',
    hasTooltip: false,
  },
];

export default function HowWeOperate() {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <section id="how-we-operate" className="py-20 bg-white" data-testid="how-we-operate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-green-900 mb-4">
            How We Operate
          </h2>
          <div className="gold-accent-line w-24 mx-auto mb-6" />
          <p className="font-inter text-gray-600 max-w-2xl mx-auto">
            We've streamlined our process to make getting your property serviced as easy as possible.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="relative text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`step-${index + 1}`}
              >
                {/* Step Number */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-600 text-white font-oswald font-bold w-8 h-8 rounded-full flex items-center justify-center text-sm">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className="bg-green-900 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 mt-4">
                  <IconComponent className="text-white" size={32} />
                </div>
                
                {/* Content */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <h3 className="font-oswald text-xl font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  {step.hasTooltip && (
                    <button
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="text-amber-600 hover:text-amber-700 p-1 rounded-full hover:bg-amber-50 transition-colors"
                      data-testid="property-assessment-info"
                      aria-label="More information about property assessment"
                    >
                      <Info size={20} />
                    </button>
                  )}
                </div>
                <p className="font-inter text-gray-600 text-sm leading-relaxed">
                  {step.description}
                </p>

                {/* Connector Line (hidden on last item and mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gray-200" />
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile-Friendly Tooltip Modal */}
        {showTooltip && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowTooltip(false)}>
            <div 
              className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl animate-fade-in-up"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Info className="text-amber-600" size={20} />
                  </div>
                  <h4 className="font-oswald text-lg font-semibold text-gray-900">
                    Property Assessment
                  </h4>
                </div>
                <button
                  onClick={() => setShowTooltip(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="font-inter text-gray-700 text-sm leading-relaxed">
                We take into consideration access, elevation of the property, rock garden beds and walkways (if loose we will discuss the potential of broken windows) and any other obstacles.
              </p>
              <button
                onClick={() => setShowTooltip(false)}
                className="mt-4 w-full bg-green-900 hover:bg-green-800 text-white font-oswald font-medium py-2 rounded-lg transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {/* Key Information Boxes */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-50 rounded-xl p-6 border border-gray-100" data-testid="info-box-minimum">
            <h4 className="font-oswald text-lg font-semibold text-green-900 mb-2">
              Minimum Charges
            </h4>
            <p className="font-inter text-gray-600 text-sm">
              We have minimum charges for all our services to ensure quality and efficiency on every job.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-xl p-6 border border-gray-100" data-testid="info-box-instant">
            <h4 className="font-oswald text-lg font-semibold text-green-900 mb-2">
              Instant Online Quotes
            </h4>
            <p className="font-inter text-gray-600 text-sm">
              Get an estimated quote instantly through our website calculator, or submit your details for a custom quote.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-xl p-6 border border-gray-100" data-testid="info-box-payment">
            <h4 className="font-oswald text-lg font-semibold text-green-900 mb-2">
              7-Day Payment Terms
            </h4>
            <p className="font-inter text-gray-600 text-sm">
              All our invoices have a 7-day payment term for your convenience.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
